/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import { MenuItem } from "@react/react-spectrum/Menu";
import { FormattedMessage } from "react-intl";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import withToolTip from "dc-preview-utils/src/HOC/withToolTip";
import { PREMIUM_ICON_TOOLTIP_ID, TOOLTIP_RIGHT_PLACEMENT } from "constants/constants";
import ContextMenuCss from "./css/ContextMenu.css";
import PremiumIndicator from "./PremiumIndicator";

class ContextMenuItem extends Component {
  constructor(props) {
    super(props);
    this.clickHandler = this.clickHandler.bind(this);
  }

  clickHandler(eventObj) {
    let eventHandler = this.props.menuItem.eventHandler;
    let args = this.props.menuItem.args || [];
    const verbId = this.props.menuItem.verbId;
    Promise.resolve(eventHandler(...args, this.props.getExtraParams(verbId))).then(() =>
      this.props.clearTouchSelectionIfPresent()
    );
    if (this.props.menuItem.stayOpenOnSelect) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
    }
  }

  onContextMenu(eventObj) {
    if (eventObj.stopPropagation) {
      eventObj.stopPropagation();
      eventObj.preventDefault();
      this.clickHandler(eventObj);
    }
  }

  getStyle() {
    const { showDividerInTop, showDividerInBottom } = this.props;
    let style = {
      whiteSpace: "nowrap",
      ...(showDividerInTop ? { borderTop: "1px solid var(--spectrum-global-color-gray-200)" } : {}),
      ...(showDividerInBottom ? { borderBottom: "1px solid var(--spectrum-global-color-gray-200)" } : {})
    };
    if (this.props.menuItem.menuItemCustomUI) {
      style.padding = 0;
    }
    const menuItemStyle = Object.assign({}, style, this.props.contextMenuItemStyle);
    return menuItemStyle;
  }

  getFormattedMessage() {
    if (this.props.formatMessage) {
      return this.props.formatMessage({ id: this.props.menuItem.title });
    }
    return <FormattedMessage id={this.props.menuItem.title} />;
  }

  renderBadge() {
    const { badge } = this.props.menuItem;
    if (badge) {
      return <div className={ContextMenuCss.contextMenuBadge}> {badge}</div>;
    }
  }

  handleMouseEnter(evt) {
    evt.currentTarget.focus();
    const mouseEvents = this.props.menuItem.mouseEvents;
    if (mouseEvents) {
      mouseEvents.onMouseEnter(evt);
    }
  }

  handleMouseLeave(evt) {
    evt.currentTarget.blur();
    const mouseEvents = this.props.menuItem.mouseEvents;
    if (mouseEvents) {
      mouseEvents.onMouseLeave(evt);
    }
  }

  handleFocus(evt) {
    const mouseEvents = this.props.menuItem.mouseEvents;
    if (mouseEvents) {
      mouseEvents.onMouseEnter(evt);
    }
  }

  handleBlur(evt) {
    const mouseEvents = this.props.menuItem.mouseEvents;
    if (mouseEvents) {
      mouseEvents.onMouseLeave(evt);
    }
  }

  renderPremiumIndicator() {
    const { shouldShowPremiumIcon, premiumIconTooltip } = this.props.menuItem;
    if (!shouldShowPremiumIcon) return null;

    if (premiumIconTooltip) {
      return <PremiumIndicator tooltip={premiumIconTooltip} />;
    }

    if (this.props.formatMessage) {
      return <PremiumIndicator tooltip={this.props.formatMessage({ id: PREMIUM_ICON_TOOLTIP_ID })} />;
    }

    return (
      <FormattedMessage id={PREMIUM_ICON_TOOLTIP_ID}>
        {tooltip => <PremiumIndicator tooltip={tooltip} />}
      </FormattedMessage>
    );
  }

  renderMenuItem = () => {
    const menuItem = this.props.menuItem.menuItemCustomUI ? (
      <MenuItem
        onClick={this.clickHandler.bind(this)}
        onContextMenu={this.onContextMenu.bind(this)}
        onMouseEnter={this.handleMouseEnter.bind(this)}
        onMouseLeave={this.handleMouseLeave.bind(this)}
        onFocus={this.handleFocus.bind(this)}
        onBlur={this.handleBlur.bind(this)}
        data-testid={this.props.menuItem.title}
        style={this.getStyle()}
        id="custom-menu-item"
        className={ContextMenuCss.contextMenuItem}
      >
        <div className={ContextMenuCss.contextMenuItemBody}>{this.props.menuItem.menuItemCustomUI}</div>
      </MenuItem>
    ) : (
      <MenuItem
        onClick={this.clickHandler.bind(this)}
        onContextMenu={this.onContextMenu.bind(this)}
        data-testid={this.props.menuItem.title}
        value={this.props.menuItem.title}
        style={this.getStyle()}
        className={ContextMenuCss.contextMenuItem}
        {...(this.props.useModernViewer && this.props.menuItem.icon
          ? { icon: <CustomIcon url={this.props.menuItem.icon} /> }
          : {})}
      >
        <div className={ContextMenuCss.contextMenuItemBody}>
          {this.getFormattedMessage()}
          {this.renderBadge()}
          {this.renderPremiumIndicator()}
        </div>
      </MenuItem>
    );
    return menuItem;
  };

  renderMenuItemWithTooltip = () => {
    const { tooltip, shouldShowPremiumIcon, title } = this.props.menuItem;
    const MenuItemComponent = withToolTip(this.renderMenuItem);
    // premium icon tooltip overlaps with main tooltip, so don't show main tooltip.
    // Also if title and tooltip are same, don't show tooltip
    const shouldShowTooltip = tooltip && !shouldShowPremiumIcon && tooltip !== title;
    return (
      <MenuItemComponent
        tooltip={
          shouldShowTooltip
            ? {
                text: tooltip,
                placement: TOOLTIP_RIGHT_PLACEMENT,
                trigger: "hover"
              }
            : {}
        }
      />
    );
  };

  render() {
    return <React.Fragment>{this.renderMenuItemWithTooltip()}</React.Fragment>;
  }
}

export { ContextMenuItem };
